import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Input, Radio, message } from "antd";

const ModalParticipantes = ({
  visible,
  onClose,
  participantes,
  onSave,
  participantesSeleccionados = [],
}) => {
  const [filtro, setFiltro] = useState("");
  const [seleccionadosKeys, setSeleccionadosKeys] = useState([]);
  const [representanteLegal, setRepresentanteLegal] = useState(null);
  const [hayCambios, setHayCambios] = useState(false);

  // Función para comparar si las listas son diferentes
  const compararSelecciones = (keysActuales, representanteActual) => {
    // Si no hay participantes seleccionados originalmente y tampoco hay selección actual
    if (participantesSeleccionados.length === 0 && keysActuales.length === 0) {
      return false;
    }

    // Si las cantidades son diferentes, hay cambios
    if (participantesSeleccionados.length !== keysActuales.length) {
      return true;
    }

    // Crear arrays comparables de la selección actual
    const seleccionActual = participantes
      .filter((p) => keysActuales.includes(p.id))
      .map((p) => ({
        id: p.id,
        es_representante_legal: representanteActual === p.id,
      }));

    // Comparar cada participante
    for (let i = 0; i < participantesSeleccionados.length; i++) {
      const original = participantesSeleccionados[i];
      const existe = seleccionActual.find(
        (actual) =>
          actual.id === original.id &&
          actual.es_representante_legal === original.es_representante_legal
      );

      if (!existe) {
        return true; // Hay cambios
      }
    }

    return false; // No hay cambios
  };

  // Inicializar estado cuando se abre el modal o cambian los participantes seleccionados
  useEffect(() => {
    if (visible && participantesSeleccionados.length > 0) {
      const keysSeleccionados = [];
      let representanteKey = null;

      participantesSeleccionados.forEach((seleccionado) => {
        const participante = participantes.find(
          (p) => p.id === seleccionado.id
        );

        if (participante) {
          keysSeleccionados.push(participante.id);
          if (seleccionado.es_representante_legal) {
            representanteKey = participante.id;
          }
        }
      });

      setSeleccionadosKeys(keysSeleccionados);
      setRepresentanteLegal(representanteKey);
      setHayCambios(false);
    } else if (visible && participantesSeleccionados.length === 0) {
      setSeleccionadosKeys([]);
      setRepresentanteLegal(null);
      setHayCambios(false);
    }
  }, [visible, participantesSeleccionados, participantes]);

  // Detectar cambios cada vez que se modifica la selección o el representante legal
  useEffect(() => {
    if (visible) {
      const cambios = compararSelecciones(
        seleccionadosKeys,
        representanteLegal
      );
      setHayCambios(cambios);
    }
  }, [seleccionadosKeys, representanteLegal, visible]);

  const participantesFiltrados = participantes.filter(
    (p) =>
      p.nombre_completo.toLowerCase().includes(filtro.toLowerCase()) ||
      (p.codigo_sirr || "").toLowerCase().includes(filtro.toLowerCase())
  );

  const rowSelection = {
    selectedRowKeys: seleccionadosKeys,
    onChange: (keys) => {
      if (keys.length > 5) {
        message.warning("Solo puedes seleccionar máximo 5 participantes");
        return;
      }

      setSeleccionadosKeys(keys);

      if (representanteLegal && !keys.includes(representanteLegal)) {
        setRepresentanteLegal(null);
      }
    },
  };

  const handleRepresentanteLegalChange = (e, recordKey) => {
    const newRepresentante = e.target.checked ? recordKey : null;

    if (newRepresentante && !seleccionadosKeys.includes(newRepresentante)) {
      message.warning(
        "Primero debes seleccionar al participante para poder marcarlo como representante legal"
      );
      return;
    }

    setRepresentanteLegal(newRepresentante);
  };

  const columns = [
    {
      title: "Código SIRR",
      dataIndex: "codigo_sirr",
      key: "codigo_sirr",
    },
    {
      title: "Nombre",
      dataIndex: "nombre_completo",
      key: "nombre_completo",
    },
    {
      title: "Identificación",
      dataIndex: "identificacion",
      key: "identificacion",
      render: (val) => val || "N/A",
    },
    {
      title: "Representante Legal o delegado",
      key: "representante_legal",
      render: (_, record) => {
        return (
          <Radio
            checked={representanteLegal === record.id}
            onChange={(e) => handleRepresentanteLegalChange(e, record.id)}
            disabled={!seleccionadosKeys.includes(record.id)}
          >
            {representanteLegal === record.id ? "Sí" : "No"}
          </Radio>
        );
      },
    },
  ];

  const handleSave = () => {
    if (seleccionadosKeys.length === 0) {
      message.warning("Debes seleccionar al menos un participante");
      return;
    }

    if (representanteLegal === null) {
      message.warning("Debes asignar un representante legal");
      return;
    }

    const seleccionados = participantes
      .filter((p) => seleccionadosKeys.includes(p.id))
      .map((p) => ({
        ...p,
        es_representante_legal: representanteLegal === p.id,
      }));

    onSave(seleccionados);
    onClose();
  };

  return (
    <Modal
      title="Selecciona los asistentes a la entrega (Máximo 5)"
      open={visible}
      onCancel={() => {
        setFiltro("");
        onClose();
      }}
      width="80vw"
      footer={[
        <Button
          key="close"
          onClick={() => {
            setFiltro("");
            onClose();
          }}
        >
          Cerrar
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          disabled={!hayCambios}
        >
          Guardar selección ({seleccionadosKeys.length}/5)
          {hayCambios && " *"}
        </Button>,
      ]}
    >
      <Input.Search
        placeholder="Buscar participante..."
        allowClear
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16, color: "#666" }}>
        <p>• Selecciona máximo 5 participantes</p>
        <p>• Debe seleccionar el representante legal o delegado</p>
        {hayCambios && (
          <p style={{ color: "#1890ff", fontWeight: "bold" }}>
            • Hay cambios sin guardar
          </p>
        )}
      </div>

      <Table
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={participantesFiltrados}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />
    </Modal>
  );
};

export default ModalParticipantes;
